<template>
    <div v-if="field" style="min-height: 500px;">

        <div class="accordion" role="fvlist">
            <b-card no-body class="mb-1">
                <b-card-header header-tag="header" class="p-1" role="fv">
                    <b-button block v-b-toggle.acc-fv-1 >All {{ field.linkedClass }} values
                        <b-badge pill variant="primary">{{ items.length }}</b-badge></b-button>
                </b-card-header>
                <b-collapse id="acc-fv-1" :visible="isEmpty" accordion="fv-accordion" role="fvpanel">
                    <b-table id="all-values" small :items="filteredValues" :fields="valueFields" primary-key="item"
                             :per-page="valuesPerPage" :current-page="valuesCurrentPage" @row-clicked="rowValueClicked"
                             :tbody-tr-class="tbodyRowClass"
                             :sort-by.sync="valuesSortBy" :sort-desc.sync="valuesSortDesc">

                        <template #top-row="">
                            <!-- Value -->
                            <td></td>
                            <!-- Label column -->
                            <td>
                                <b-form-input id="value-filter" v-model="selectVal['label']" type="search" placeholder="Type to Search"></b-form-input>
                            </td>
                            <!-- Position column -->
                            <td></td>
                            <!-- Selected column -->
                            <td>
                                <b-checkbox :checked="isAllRowSelected()" @change="allRowsClicked()"></b-checkbox>
                            </td>
                        </template>

                        <!-- Selected column -->
                        <template v-slot:cell(selected)="{ item }" >
                            <b-checkbox :checked="isValueSelected(item)" @change="rowValueClicked(item)"></b-checkbox>
                        </template>
                    </b-table>

                    <b-pagination v-model="valuesCurrentPage" :total-rows="valuesRows" :per-page="valuesPerPage" align="fill"
                                  aria-controls="all-values"></b-pagination>
                </b-collapse>
            </b-card>

            <b-card no-body class="mb-1">
                <b-card-header header-tag="header" class="p-1" role="fv">
                    <b-button block v-b-toggle.acc-fv-2>Selected {{ field.linkedClass }} values
                        <b-badge pill variant="primary">{{ Object.keys(this.field.fieldValues).length }}</b-badge></b-button>
                </b-card-header>
                <b-collapse id="acc-fv-2" :visible="!isEmpty" accordion="fv-accordion" role="fvpanel">
                    <b-table id="selected-values" small borderless responsive :items="orderByPosition(field.fieldValues)" :fields="valueFields">
                        <template v-slot:cell(value)="rowValue">
                            <b-form-input v-model="rowValue.item.value" required disabled :state="containValue(rowValue.item.value)"/>
                        </template>
                        <template v-slot:cell(label)="rowValue">
                            <b-form-input v-model="rowValue.item.label" required/>
                        </template>
                        <template v-slot:cell(position)="rowValue">
                            <b-form-spinbutton v-model="rowValue.item.position" required/>
                        </template>
                        <template v-slot:cell(selected)="rowValue">
                            <b-link @click="removeValue(field, rowValue.item.value)"
                                    class="h4 text-danger">
                                <b-icon icon="x-circle"></b-icon>
                            </b-link>
                        </template>
                    </b-table>
                </b-collapse>
            </b-card>
        </div>


    </div>
</template>

<script>
import _ from "lodash";

export default {
    name: "PortalFieldValues",
    props: {
        currentPortal: Object,
        field: Object,
        items: Array,
    },
    data() {
        return {
            fields: [
                {key: 'value', sortable: true, label: 'Id'},
                {key: 'label', sortable: true},
                {key: 'position', sortable: true, class: 'text-center align-middle col-icon'},
                {key: 'selected', sortable: false, label: '', class: 'text-center align-middle col-icon'},
            ],
            valuesPerPage: 10,
            valuesCurrentPage: 1,
            valuesSortBy: 'label',
            valuesSortDesc: false,
            selectVal: {
                label: undefined,
                selected: undefined
            },
            isEmpty: Object.keys(this.field.fieldValues).length === 0
        }
    },
    computed: {
        valuesRows() {
            return this.filteredValues.length
        },
        filteredValues() {
            return this.items.filter(item => {
                let keep = true;

                // This is a basic equality filter. What I did in the actual code was to have an object with filter functions for each key. If a key was missing, it defaulted to straight equality.
                this.valueFields.forEach(field => {
                    let key = field.key;
                    if (key === 'label') {
                        keep = keep && (this.selectVal[key] === undefined ||
                            String(item[key]).toLowerCase().trim().includes(this.selectVal[key].toLowerCase().trim()));
                    } else {
                        keep = keep && (this.selectVal[key] === undefined ||
                            item[key] === this.selectVal[key]);
                    }
                });

                return keep;
            });
        },
        valueFields() {
            return this.fields;
        },
        valueOptions() {
            // This could be simplified if the select was it's own component.
            const options = {};
            this.valueFields.forEach(field => {
                if (field.key !== 'label') {
                    const vals = this.items.map(item => item[field.key]);

                    // Cheap and efficient unique.
                    options[field.key] = Array.from(new Set(vals));
                }
            });

            return options;
        },
        selectedValues() {
            return this.orderByPosition(Object.values(this.field.fieldValues));
        },
    },
    methods: {
        orderByPosition(list) {
            return _.orderBy(list, 'position')
        },
        isValueSelected(item) {
            return item.value in this.field.fieldValues;
        },
        tbodyRowClass(item) {
            if(item && this.isValueSelected(item)){
                return ["b-table-row-selected", "table-primary", "cursor-pointer"]
            } else {
                return ["cursor-pointer"]
            }
        },
        addValue(field, item) {
            if (!field.fieldValues) {
                field.fieldValues = {};
            }
            this.$set(field.fieldValues, item.value, {value: item.value, label: item.label, position: item.position});
        },
        removeValue(field, valueId) {
            this.$delete(field.fieldValues, valueId);
        },
        rowValueClicked(item) {
            if (item && this.isValueSelected(item)) {
                this.removeValue(this.field, item.value);
            } else {
                this.addValue(this.field, item);
            }
        },
        isAllRowSelected() {
            return this.field.fieldValues != null && this.items.length === Object.keys(this.field.fieldValues).length;
        },
        allRowsClicked() {
            if (this.isAllRowSelected()) {
                // Unselected all
                this.field.fieldValues = Object.assign({}, {});
            } else {
                // Selected all
                this.field.fieldValues = Object.assign({}, {});
                this.filteredValues.forEach(item => {
                    this.addValue(this.field, item);
                });
            }
        },
        containValue(value) {
            if (!this.items || this.items.length === 0) {
                return null;
            }
            var arrR = this.items.filter(function(item){
                return (item.value == value)
            });
            if (arrR.length) {
                return true;
            } else {
                return false;
            }
        },
    }
}
</script>

<style lang="scss">
    .cursor-pointer{
        cursor: pointer;
    }

    .col-type {
        width: 100px;
    }
</style>